import { useState, useEffect, useRef } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6v1lfd1l1ubpb129b4g';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    inquiry: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-contact').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('이름, 이메일, 문의 내용은 필수 항목입니다.');
      return;
    }
    if (formData.message.length > 500) {
      setError('문의 내용은 500자 이내로 작성해주세요.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ company: '', name: '', email: '', phone: '', inquiry: '', message: '' });
      } else {
        setError('제출에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } catch {
      setError('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#F8F6F2] py-28" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className="fade-contact text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <span className="inline-block text-xs font-bold tracking-widest text-[#C0392B] border border-[#C0392B]/30 px-3 py-1 rounded-full mb-4">
            CONTACT US
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] leading-tight">
            최고의 산업설비 솔루션을 위해<br />
            <span className="text-[#C0392B]">언제든 문의</span>해 주세요
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto">
            전문 상담팀이 빠르게 답변드립니다.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Contact Info */}
          <div
            className="fade-contact"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s' }}
          >
            {/* Big Card */}
            <div className="bg-[#1C1C1C] rounded-2xl p-10 text-white mb-6">
              <div className="text-4xl text-[#E8C27A] font-black mb-6 leading-none">&ldquo;</div>
              <p className="text-xl font-bold leading-relaxed mb-8">
                산업 현장의 모든 설비 고민,<br />
                에스제이엔지니어링에 맡겨주세요.
              </p>
              <div className="space-y-5">
                {[
                  { icon: 'ri-building-line', label: '회사명', val: '(주)에스제이엔지니어링' },
                  { icon: 'ri-map-pin-line', label: '주소', val: '경기도 안산시 단원구\n엠티브이10로10번길9 (15656)' },
                  { icon: 'ri-phone-line', label: '전화', val: '031-404-3790' },
                  { icon: 'ri-smartphone-line', label: '모바일', val: '010-3790-5664' },
                  { icon: 'ri-printer-line', label: '팩스', val: '031-404-6480' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#E8C27A]`} />
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">{item.label}</p>
                      <p className="text-sm font-semibold whitespace-pre-line">{item.val}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-52">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.0!2d126.796!3d37.323!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357b699f7a2d2aad%3A0x999999999!2z6rK96riw64-EIOyEseyLnCDri6zsm5DqtZAg7JWM7Yq47KeA7IucMTDroZwyMeq4uA!5e0!3m2!1sko!2skr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="에스제이엔지니어링 위치"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="fade-contact"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s' }}
          >
            <div className="bg-white rounded-2xl p-8 lg:p-10">
              <h3 className="text-xl font-black text-[#1C1C1C] mb-7">문의 접수</h3>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600" />
                  </div>
                  <p className="text-lg font-bold text-[#1C1C1C] mb-2">문의가 접수되었습니다!</p>
                  <p className="text-sm text-gray-500">빠른 시일 내에 연락드리겠습니다.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 px-6 py-2.5 rounded-full bg-[#C0392B] text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-[#A93226] transition-colors"
                  >
                    추가 문의하기
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">회사명</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="회사명 입력"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        성함 <span className="text-[#C0392B]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="성함 입력"
                        required
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      이메일 <span className="text-[#C0392B]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="이메일 주소 입력"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">연락처</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="전화번호 입력"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">문의 유형</label>
                    <select
                      name="inquiry"
                      value={formData.inquiry}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors cursor-pointer bg-white"
                    >
                      <option value="">문의 유형 선택</option>
                      <option value="산업기계설비">산업기계설비 문의</option>
                      <option value="도장설비">도장설비 문의</option>
                      <option value="맞춤형 솔루션">맞춤형 솔루션 문의</option>
                      <option value="견적 요청">견적 요청</option>
                      <option value="A/S">A/S 문의</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      문의 내용 <span className="text-[#C0392B]">*</span>
                      <span className="ml-2 font-normal text-gray-400">({formData.message.length}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="문의하실 내용을 입력해 주세요. (최대 500자)"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C0392B] transition-colors resize-none"
                    />
                  </div>

                  {error && (
                    <p className="text-xs text-[#C0392B] bg-red-50 rounded-lg px-4 py-2">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-[#C0392B] text-white font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#A93226] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {submitting ? '제출 중...' : '문의 접수하기'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
